<script setup lang="ts">
import { ref } from 'vue'
import ChatApp from './components/ChatApp.vue'
import ReleaseNotes from './components/ReleaseNotes.vue'
import { useVersion } from './composables/useVersion'
import { useZenMode } from './composables/useZenMode'

// Get the current app version
const { version } = useVersion()

// Get zenMode state
const { isZenMode } = useZenMode()

// State to control release notes modal visibility
const showReleaseNotes = ref(false)

// Method to toggle release notes visibility
function toggleReleaseNotes() {
  showReleaseNotes.value = !showReleaseNotes.value
}
</script>

<template>
  <div class="app-container">
    <ChatApp />

    <!-- Release notes button (hidden in Zen Mode) -->
    <div v-if="!isZenMode" class="release-notes-button" @click="toggleReleaseNotes">
      <div class="version-badge">v{{ version || '1.0.0' }}</div>
    </div>

    <!-- Social media icons footer (hidden in Zen Mode) -->
    <div v-if="!isZenMode" class="social-footer">
      <a href="https://twitter.com/teddykim" target="_blank" aria-label="X (formerly Twitter)">
        <svg class="social-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path
            d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
          />
        </svg>
      </a>
      <a href="https://tiktok.com/@_teddy_kim" target="_blank" aria-label="TikTok">
        <svg class="social-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path
            d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"
          />
        </svg>
      </a>
      <a href="https://www.youtube.com/@TeddyKim/" target="_blank" aria-label="YouTube">
        <svg class="social-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path
            d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
          />
        </svg>
      </a>
    </div>
    
    <!-- Release Notes Modal -->
    <ReleaseNotes :show="showReleaseNotes" @close="showReleaseNotes = false" />
  </div>
</template>

<style>
html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  background-color: #465970 !important; /* Greyish blue - subtle complement to the chat UI */
  color: #f7fafc !important; /* Light text color */
}

body {
  color-scheme: dark;
}

:root {
  --color-background: #465970;
  --color-text: #f7fafc;
}

.app-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

/* Social footer */
.social-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 15px 0;
  margin-top: 10px;
}

.social-icon {
  width: 24px;
  height: 24px;
  fill: #90c0ff; /* Soft blue that complements the chat window's left message color */
  transition: fill 0.2s;
}

.social-icon:hover {
  fill: #ff9090; /* Pinkish color that complements the chat window's right message color */
}

/* Release notes button */
.release-notes-button {
  position: fixed;
  bottom: 80px;
  right: 20px;
  cursor: pointer;
  z-index: 999; /* Higher z-index to ensure visibility */
  pointer-events: auto;
}

.version-badge {
  background-color: rgba(0, 132, 255, 0.8);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  display: inline-block;
}

.version-badge:hover {
  background-color: rgba(0, 132, 255, 1);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .release-notes-button {
    top: 10px; /* Move to top of screen instead of bottom */
    right: 10px;
    bottom: auto; /* Remove bottom positioning */
  }
  
  .version-badge {
    padding: 4px 8px;
    font-size: 11px;
    background-color: rgba(0, 132, 255, 0.7); /* Slightly more transparent */
  }
}
</style>